/** Pure state logic for the user-choice HITL panel (supports batched questions). */

export interface Choice {
	id: string;
	label: string;
	description?: string;
}

export interface QuestionDef {
	question: string;
	context?: string;
	choices: Choice[];
	multi_select?: boolean;
}

export interface UserChoiceState {
	readonly selectedIds: ReadonlySet<string>;
	readonly freeText: string;
	readonly multiSelect: boolean;
}

export interface UserChoiceResult {
	selected_ids: string[];
	free_text: string | null;
}

/** Batched state: tracks answers across multiple questions. */
export interface BatchState {
	readonly questions: readonly QuestionDef[];
	readonly currentIndex: number;
	readonly choices: readonly UserChoiceState[];
}

export const FREE_TEXT_ID = "__free_text__";

/**
 * 選択肢ラベルが「自由入力」を意図したものか判定する。エージェントが choices に
 * 「その他（自由入力）」等を含めると組み込みの自由入力欄と二重化するため、これらは
 * 組み込み欄へ統合する (この関数で検出して除去する)。
 */
function isFreeTextChoice(label: unknown): boolean {
	if (typeof label !== "string") return false;
	const l = label.trim();
	return /自由入力|自由記述/.test(l) || /^その他([（(].*[)）])?$/.test(l);
}

/**
 * 1 質問を正規化する。組み込みの自由入力欄 (「その他」) は常に表示されるため、
 * エージェントが追加した自由入力系の選択肢は除去して「その他」を 1 つに統一する。
 */
function sanitizeQuestion(question: QuestionDef): QuestionDef {
	if (!Array.isArray(question.choices)) return question;
	const realChoices = question.choices.filter((c) => !isFreeTextChoice(c?.label));
	if (realChoices.length === question.choices.length) return question;
	return { ...question, choices: realChoices };
}

/**
 * tool 入力の `questions` を `QuestionDef[]` へ正規化する。
 * 配列以外 (単一質問オブジェクト・JSON 文字列・欠落) でもクラッシュせず空配列等へ畳む。
 * あわせて自由入力系の重複選択肢を組み込みの自由入力欄へ統合する。
 */
export function normalizeQuestions(raw: unknown): QuestionDef[] {
	if (Array.isArray(raw)) return raw.map((q) => sanitizeQuestion(q as QuestionDef));
	if (typeof raw === "string") {
		try {
			return normalizeQuestions(JSON.parse(raw));
		} catch {
			return [];
		}
	}
	if (raw && typeof raw === "object" && "question" in raw) {
		return [sanitizeQuestion(raw as QuestionDef)];
	}
	return [];
}

export function createInitialState(multiSelect: boolean): UserChoiceState {
	return {
		selectedIds: new Set(),
		freeText: "",
		multiSelect,
	};
}

export function createBatchState(questions: readonly QuestionDef[]): BatchState {
	return {
		questions,
		currentIndex: 0,
		choices: questions.map((question) => createInitialState(question.multi_select ?? false)),
	};
}

export function goToQuestion(batch: BatchState, index: number): BatchState {
	if (batch.questions.length === 0) return batch;

	const clampedIndex = Math.min(Math.max(index, 0), batch.questions.length - 1);
	if (clampedIndex === batch.currentIndex) return batch;

	return { ...batch, currentIndex: clampedIndex };
}

export function updateCurrentChoice(
	batch: BatchState,
	updater: (state: UserChoiceState) => UserChoiceState,
): BatchState {
	const currentChoice = batch.choices[batch.currentIndex];
	if (!currentChoice) return batch;

	const nextChoice = updater(currentChoice);
	if (nextChoice === currentChoice) return batch;

	const choices = [...batch.choices];
	choices[batch.currentIndex] = nextChoice;
	return { ...batch, choices };
}

export function isLastQuestion(batch: BatchState): boolean {
	return batch.currentIndex >= batch.questions.length - 1;
}

export function buildAllResults(batch: BatchState): UserChoiceResult[] {
	return batch.choices.map((state, index) => buildResult(state, batch.questions[index]));
}

export function allAnswered(batch: BatchState): boolean {
	return batch.choices.length > 0 && batch.choices.every(canSubmit);
}

/**
 * 選択を付け外しする。「その他」もほかの選択肢と同じく扱い、複数選択なら併せて選べる。
 * 単一選択はラジオボタンと同じく、選択中のものを押しても外さない。
 * 入力した文字は「その他」が外れても残し、選び直したときに戻す (送信には含めない)。
 */
export function toggleChoice(state: UserChoiceState, choiceId: string): UserChoiceState {
	const { selectedIds, multiSelect } = state;

	if (selectedIds.has(choiceId)) {
		if (!multiSelect) return state;
		const next = new Set(selectedIds);
		next.delete(choiceId);
		return { ...state, selectedIds: next };
	}

	if (multiSelect) {
		return { ...state, selectedIds: new Set([...selectedIds, choiceId]) };
	}

	return { ...state, selectedIds: new Set([choiceId]) };
}

/** 入力欄は「その他」を選んでいる間だけ出るため、選択は変えない。 */
export function setFreeText(state: UserChoiceState, text: string): UserChoiceState {
	return { ...state, freeText: text };
}

export function canSubmit(state: UserChoiceState): boolean {
	if (state.selectedIds.size === 0) return false;
	if (state.selectedIds.has(FREE_TEXT_ID) && state.freeText.trim() === "") {
		return false;
	}
	return true;
}

/** 画面に並ぶ順の選択肢 id。「その他」は最後。 */
function optionIds(question: QuestionDef): string[] {
	return [...new Set([...question.choices.map((choice) => choice.id), FREE_TEXT_ID])];
}

export function buildResult(state: UserChoiceState, question: QuestionDef): UserChoiceResult {
	const ids = optionIds(question).filter((id) => state.selectedIds.has(id));
	return {
		selected_ids: ids,
		free_text: ids.includes(FREE_TEXT_ID) ? state.freeText : null,
	};
}
